import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel } from '../store/studioStore';

const ARROWS = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/**
 * useKeyboard
 * Gère les raccourcis clavier globaux de l'éditeur.
 *
 * @param {function} onZoom       (delta, cx, cy) => void
 * @param {function} scheduleSave () => void
 */
export function useKeyboard(onZoom, scheduleSave) {
  const dispatch = useDispatch();
  const selection = useSelector(sel.selection);
  const historyIndex = useSelector(sel.historyIndex);
  const zoom = useSelector(sel.zoom);
  const arrowTimer = useRef(null);

  useEffect(() => {
    function onKeyDown(e) {
      const inInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
        document.activeElement?.tagName
      );
      const editing = document.activeElement?.contentEditable === 'true';

      // Escape — ferme les modals (géré par chaque modal) + sort du text edit
      if (e.key === 'Escape') return;

      // Undo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch(studioActions.restoreHistory(historyIndex - 1));
        return;
      }

      // Zoom clavier
      if ((e.ctrlKey || e.metaKey) && ['=', '+'].includes(e.key)) {
        e.preventDefault();
        const area = document.getElementById('canvasArea');
        onZoom(zoom + 0.1, area?.clientWidth / 2, area?.clientHeight / 2);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        const area = document.getElementById('canvasArea');
        onZoom(zoom - 0.1, area?.clientWidth / 2, area?.clientHeight / 2);
        return;
      }

      // Supprimer la sélection
      if (e.key === 'Delete' && !editing) {
        if (selection.length > 0) {
          dispatch(studioActions.deleteSelected());
          dispatch(studioActions.pushHistory());
          scheduleSave();
        }
        return;
      }

      // Flèches — déplacer les éléments sélectionnés
      if (ARROWS[e.key] && selection.length > 0 && !inInput && !editing) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const [dx, dy] = ARROWS[e.key].map((v) => v * step);
        dispatch(
          studioActions.updateElements(
            selection.map((id) => ({ id, changes: {} })) // handled below via thunk pattern
          )
        );
        // On dispatch directement un batch move
        dispatch({
          type: 'studio/batchMove',
          payload: { ids: selection, dx, dy },
        });

        clearTimeout(arrowTimer.current);
        arrowTimer.current = setTimeout(() => {
          dispatch(studioActions.pushHistory());
          scheduleSave();
        }, 400);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(arrowTimer.current);
    };
  }, [dispatch, selection, historyIndex, zoom, onZoom, scheduleSave]);
}
